import * as React from "react";
import { cn } from "@/lib/utils";

export type TextShimmerProps = {
  /** Plain text, or mixed content (icons, spans) — the highlight is sized from the text inside. */
  children: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  /** Sweep duration in seconds. */
  duration?: number;
  /** Highlight width, in px per character. */
  spread?: number;
  style?: React.CSSProperties;
};

/** Characters of text under a node — sizes the highlight for mixed children too. */
function textLength(node: React.ReactNode): number {
  if (node == null || typeof node === "boolean") return 0;
  if (typeof node === "string" || typeof node === "number") return String(node).length;
  if (Array.isArray(node)) return node.reduce<number>((n, child) => n + textLength(child), 0);
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textLength(node.props.children);
  return 0;
}

/**
 * Text with a sweeping gradient highlight (loading-ui.com text-shimmer),
 * reimplemented on a pure CSS animation (`--animate-text-shimmer` in
 * index.css) — no JS on the animation path, nothing re-renders per frame.
 */
function TextShimmerComponent({
  children,
  as: Component = "span",
  className,
  duration = 1.5,
  spread = 2,
  style,
}: TextShimmerProps) {
  const dynamicSpread = Math.max(1, textLength(children)) * spread;

  return (
    <Component
      className={cn(
        "relative inline-block animate-text-shimmer bg-size-[250%_100%,auto] bg-clip-text",
        "[-webkit-text-fill-color:transparent]",
        "[background-repeat:no-repeat,padding-box] [--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--base-gradient-color),#0000_calc(50%+var(--spread)))]",
        className
      )}
      style={
        {
          ...style,
          "--spread": `${dynamicSpread}px`,
          "--base-color": "color-mix(in oklab, currentColor 55%, transparent)",
          "--base-gradient-color": "currentColor",
          backgroundImage: `var(--bg), linear-gradient(var(--base-color), var(--base-color))`,
          animationDuration: `${duration}s`,
        } as React.CSSProperties
      }
    >
      {children}
    </Component>
  );
}

export const TextShimmer = React.memo(TextShimmerComponent);
